import React, { useState, useRef, useEffect, useCallback } from 'react';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  ASSET KENNIS-KLUIS — Embedded AI Tab for AssetDashboard
//  Uses /ask/asset endpoint with asset_id scoping
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import kennisKluis from '../services/kennisKluisService';
import AiMelding from './AiMelding';

const AssetKennisKluis = ({ assetId, instanceName, currentUser }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [contextInfo, setContextInfo] = useState(null);
    const chatEndRef = useRef(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Load asset context on mount
    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const data = await kennisKluis.assetContext(assetId);
                setContextInfo(data);
            } catch (err) {
                console.warn('Asset context laden mislukt:', err);
            }
        })();
    }, [assetId]);

    const handleSend = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || loading) return;

        setMessages(prev => [...prev, { role: 'user', text: trimmed, time: new Date() }]);
        setInput('');
        setLoading(true);

        try {
            const data = await kennisKluis.askAsset({
                vraag: trimmed,
                assetId,
                user: currentUser?.email || 'monteur@raconworks.nl'
            });

            setMessages(prev => [...prev, {
                role: 'ai',
                text: data.antwoord || 'Geen antwoord.',
                bronnen: data.bronnen || [],
                tokens: data.tokens_used,
                feedbackId: data.feedback_id || null,
                rating: null,
                time: new Date()
            }]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'error',
                text: err.unauthorized
                    ? 'Geen toegang tot de Kennis-Kluis. Meld dit bij uw beheerder.'
                    : `Kennis-Kluis niet bereikbaar: ${err.message}`,
                time: new Date()
            }]);
        } finally {
            setLoading(false);
        }
    }, [input, loading, assetId, currentUser]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Feedback handlers
    const sendFeedback = useCallback(async (msgIdx, rating) => {
        const msg = messages[msgIdx];
        if (!msg?.feedbackId) return;

        try {
            await kennisKluis.sendFeedback({ feedbackId: msg.feedbackId, rating });
            setMessages(prev => prev.map((m, i) =>
                i === msgIdx ? { ...m, rating } : m
            ));
        } catch (err) {
            console.error('Feedback fout:', err);
        }
    }, [messages]);

    const sendCorrectie = useCallback(async (msgIdx, correctie) => {
        const msg = messages[msgIdx];
        if (!msg?.feedbackId || !correctie.trim()) return;

        try {
            await kennisKluis.sendFeedback({
                feedbackId: msg.feedbackId,
                rating: -1,
                correctie: correctie.trim()
            });
            setMessages(prev => prev.map((m, i) =>
                i === msgIdx ? { ...m, rating: -1, correctie: correctie.trim() } : m
            ));
        } catch (err) {
            console.error('Correctie fout:', err);
        }
    }, [messages]);

    // Quick question chips
    const quickQuestions = [
        `Wat is de resetprocedure voor ${instanceName || assetId}?`,
        `Welke onderdelen zijn nodig voor onderhoud?`,
        `Wat zijn de laatste storingen?`,
        `Wat is het onderhoudsinterval?`
    ];

    return (
        <div className="space-y-4">
            {/* Context bar */}
            <div className="flex items-center gap-3 bg-gradient-to-r from-purple-900/30 to-slate-800 rounded-xl p-3 border border-purple-700/30">
                <div className="w-10 h-10 rounded-xl bg-purple-600/30 flex items-center justify-center">
                    <span className="text-xl">🧠</span>
                </div>
                <div className="flex-1">
                    <p className="text-sm font-bold text-purple-300">
                        AI Assistent — {instanceName || assetId}
                    </p>
                    <p className="text-[10px] text-slate-500">
                        {contextInfo
                            ? `${contextInfo.chunks || 0} kennisbronnen geladen`
                            : 'Asset-specifieke context wordt geladen...'}
                    </p>
                </div>
            </div>

            {/* Verplichte AI-melding, art. 50 lid 1 AI-verordening */}
            <AiMelding compact />

            {/* Quick Questions */}
            {messages.length === 0 && (
                <div className="grid grid-cols-1 gap-2">
                    {quickQuestions.map((q, idx) => (
                        <button
                            key={idx}
                            onClick={() => { setInput(q); }}
                            className="text-left p-2.5 rounded-xl bg-slate-900 border border-slate-700 hover:border-purple-500/50 hover:bg-slate-800 transition-all text-xs text-slate-300 hover:text-purple-300 group"
                        >
                            <span className="text-purple-500/50 mr-2 group-hover:text-purple-400">💬</span>
                            {q}
                        </button>
                    ))}
                </div>
            )}

            {/* Chat Messages */}
            <div className="bg-slate-900/60 rounded-xl border border-slate-700/50 p-3 min-h-[200px] max-h-[350px] overflow-y-auto space-y-3">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-xl px-3 py-2 ${msg.role === 'user'
                            ? 'bg-purple-600 text-white'
                            : msg.role === 'error'
                                ? 'bg-red-900/40 border border-red-700/50 text-red-200'
                                : 'bg-slate-800 text-slate-100 border border-slate-700/50'
                            }`}>
                            <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                            {msg.bronnen && msg.bronnen.length > 0 && (
                                <div className="mt-1.5 pt-1.5 border-t border-slate-600/30">
                                    {msg.bronnen.map((b, i) => (
                                        <span key={i} className="inline-block text-[9px] bg-slate-900 text-purple-300 px-1.5 py-0.5 rounded mr-1 mb-0.5">
                                            📄 {b.bron || b.source}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {/* Feedback Buttons */}
                            {msg.role === 'ai' && msg.feedbackId && (
                                <div className="mt-2 pt-2 border-t border-slate-600/30">
                                    {msg.rating === null ? (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => sendFeedback(idx, 1)}
                                                className="text-[10px] px-2 py-0.5 rounded bg-slate-700 hover:bg-green-800 text-slate-400 hover:text-green-300 transition-all"
                                            >
                                                👍 Correct
                                            </button>
                                            <button
                                                onClick={() => sendFeedback(idx, -1)}
                                                className="text-[10px] px-2 py-0.5 rounded bg-slate-700 hover:bg-red-900 text-slate-400 hover:text-red-300 transition-all"
                                            >
                                                👎 Fout
                                            </button>
                                        </div>
                                    ) : msg.rating === 1 ? (
                                        <span className="text-[10px] text-green-400">✅ Bedankt voor de feedback</span>
                                    ) : (
                                        <div className="space-y-1">
                                            <span className="text-[10px] text-red-400">❌ Gemarkeerd als fout</span>
                                            {!msg.correctie && (
                                                <div className="flex gap-1 mt-1">
                                                    <input
                                                        type="text"
                                                        placeholder="Wat is het goede antwoord?"
                                                        className="flex-1 text-[10px] bg-slate-900 border border-slate-600 rounded px-2 py-0.5 text-white"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                sendCorrectie(idx, e.target.value);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {msg.correctie && (
                                                <span className="text-[10px] text-green-400 block">✅ Correctie opgeslagen</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-slate-800 rounded-xl px-3 py-2 border border-slate-700/50">
                            <span className="loading loading-dots loading-xs text-purple-400"></span>
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="flex gap-2">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Vraag iets over ${instanceName || assetId}...`}
                    className="flex-1 input input-sm input-bordered bg-slate-900 text-white border-slate-700 focus:border-purple-500 text-xs"
                />
                <button
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    className="btn btn-sm bg-purple-600 hover:bg-purple-500 text-white border-none shadow-lg"
                >
                    {loading ? '⏳' : '🚀'}
                </button>
            </div>
        </div>
    );
};

export default AssetKennisKluis;
